import type { ApexOptions } from "apexcharts";
import ptBr from "apexcharts/dist/locales/pt-br.json";
import { formatBRL, formatQty, toCsv } from "@cx/domain";
import { count, money, qty } from "./labels";

export type ChartUnit = "money" | "count" | "qty";
export type ChartSeries = { name: string; data: (number | null)[] };

/** Rótulo do eixo e da dica na unidade da série (centavos, q4 ou contagem). */
export function unitFormatter(unit: ChartUnit): (v: number | null | undefined) => string {
  if (unit === "money") return (v) => money(v === null || v === undefined ? v : Math.round(v));
  if (unit === "qty") return (v) => qty(v === null || v === undefined ? v : Math.round(v));
  return (v) => count(v === null || v === undefined ? v : Math.round(v));
}

/** CSV para planilha em pt-BR: vírgula decimal, sem "R$" e sem separador de milhar. */
export function csvValue(unit: ChartUnit): (v?: number | null) => string {
  return (v) => {
    if (v === null || v === undefined) return "";
    const n = Math.round(v);
    if (unit === "money") return formatBRL(n).replace("R$ ", "").replaceAll(".", "");
    if (unit === "qty") return formatQty(n).replaceAll(".", "");
    return String(n);
  };
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Escapa texto para HTML: o ApexCharts 4.7 grava a legenda e a dica com innerHTML (Legend.js, tooltip/Labels.js). */
export const escapeHtml = (v: unknown): string => String(v).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]!);

/**
 * CSV do gráfico gerado no app a partir dos mesmos dados da tabela: o do ApexCharts não neutraliza fórmula (nome de
 * vendedor, produto ou canal começando com "=" viraria fórmula na planilha). ";", vírgula decimal e BOM UTF-8.
 */
export function chartCsv(categories: string[], series: ChartSeries[], unit: ChartUnit): string {
  const v = csvValue(unit);
  return toCsv([
    ["Categoria", ...series.map((s) => s.name)],
    ...categories.map((c, i) => [c, ...series.map((s) => v(s.data[i]))]),
  ]);
}

/**
 * Opções do ApexCharts com o tema do Coup: idioma pt-BR, menu Baixar SVG/PNG (o CSV do menu fica escondido no CSS: o
 * ChartCard tem o próprio, com `chartCsv`), sem zoom, valores formatados na unidade. Nome de série e categoria passam
 * por `escapeHtml` em todo lugar que o ApexCharts monta com innerHTML (legenda, título e linhas da dica); o eixo é texto
 * SVG (textContent) e fica sem escape. Cores pelas variáveis do Bootstrap, lidas no momento da montagem.
 */
export function chartOptions(o: {
  type: "line" | "bar" | "area";
  categories: string[];
  unit: ChartUnit;
  filename: string;
  stacked?: boolean;
  colors?: string[];
}): ApexOptions {
  const fmt = unitFormatter(o.unit);
  return {
    chart: {
      type: o.type,
      stacked: o.stacked ?? false,
      fontFamily: "inherit",
      foreColor: "var(--bs-secondary-color)",
      locales: [ptBr],
      defaultLocale: "pt-br",
      animations: { enabled: false },
      zoom: { enabled: false },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          svg: { filename: o.filename },
          png: { filename: o.filename },
        },
      },
    },
    colors: o.colors ?? [
      "#0d6efd",
      "#adb5bd",
      "#198754",
      "#fd7e14",
      "#6f42c1",
      "#20c997",
      "#d63384",
      "#ffc107",
    ],
    dataLabels: { enabled: false },
    stroke: { width: o.type === "bar" ? 0 : 2, curve: "straight" },
    legend: { position: "top", horizontalAlign: "left", formatter: (name: string) => escapeHtml(name) },
    grid: { borderColor: "var(--bs-border-color)" },
    xaxis: { categories: o.categories, tooltip: { enabled: false } },
    yaxis: { labels: { formatter: (v: number) => fmt(v) } },
    tooltip: {
      x: { formatter: (v: unknown) => escapeHtml(v) },
      y: { formatter: (v: number) => fmt(v), title: { formatter: (name: string) => escapeHtml(name) } },
    },
    noData: { text: "Sem vendas no período" },
  };
}
